/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSV_DATA_DOWNLOAD, MARKETPLACE_ORDERS_MUTATION } from "./common/marketplace/constants";
import { decodeHtml } from "../utilities/utils";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import safetagLogo from "../media_assets/safetag_icon.png";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { pushMessage } from "../reducers/messagingReducer";
import { useMutation } from "@apollo/client";
import { getPartnersInfo } from "./common/marketplace/functions";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { marketplaceDataLoadStyle } from "./common/marketplace/styles";
import { Checkbox } from "primereact/checkbox";
import { Paginator } from 'primereact/paginator'

const domainGridStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    .domain-name {
        padding-left: 1em;
    }
`
const datatableStyles = css`
    .p-datatable-thead {
        tr {
            height: 80px;
            th {
                border-right: 1px solid transparent !important;
                &:last-of-type, &:first-of-type {
                    border-right: 1px solid #e9ecef !important;
                }
            }
        }
    }
    .p-datatable-tbody {
        tr {
            height: 100px; 
            max-height: 100px;
            td {
                border-right: 1px solid transparent !important;
                &:last-of-type, &:first-of-type {
                    border-right: 1px solid #e9ecef !important;
                }
            }
        }
    }

`
const domainDescriptionStyles = css`
    .p-button {
        padding: 0 !important;
    }
`
/**
 * @typedef {Object} MarketplaceDomainConfig
 * @property {string} marketplace_domain_name - The name of the marketplace domain (e.g., "21ninety").
 * @property {string} marketplace_domain_url - The full URL of the marketplace domain.
 * @property {string} marketplace_impressions - A string representing the range of monthly impressions (e.g., "1MM - 5MM").
 * @property {string} marketplace_domain_description - A textual description of the marketplace.
 * @property {string} marketplace_domain_logo - A URL pointing to the marketplace's logo image.
 * @property {boolean} enable_safetag - Indicates whether safetag is enabled.
 * @property {string} marketplace_safetag_words - A string of safetag words (could be empty).
 * @property {Array<MarketplaceNetwork>} selected_networks - A list of selected networks with their name and code.
 * @property {boolean} allows_video - Indicates if video ads are allowed.
 * @property {Array<string>} display_sizes - An array of allowed ad display sizes (e.g., "970x250").
 * @property {string} id - A unique identifier for the marketplace.
 */

/**
 * @typedef {Object} MarketplaceNetwork
 * @property {string} name - The name of the network (e.g., "Bipoc").
 * @property {number} code - A numeric code identifying the network.
 */

/**
 * 
 * @param {Object} propsObject 
 * @param {Array<MarketplaceDomainConfig>} propsObject.domainsColl
 * @param {boolean} propsObject.hasFinishedRetrievingDomains
 * @param {boolean} propsObject.enableDomainSelection
 * @param {Set<string>} propsObject.selectedDomains
 * @param {function} propsObject.domainSelectionHandler
 * @param {MarketplaceOrder} propsObject.marketplaceOrder
 * @returns 
 */
const DomainGrid = ({
    domainsColl,
    hasFinishedRetrievingDomains,
    enableDomainSelection,
    selectedDomains,
    domainSelectionHandler,
    marketplaceOrder
}) => {
    /**
     * 
     * @param {MarketplaceDomainConfig} rowData 
     * @returns 
     */
    const marketplaceDomainImageTemplate = rowData => {
        let domainId = rowData.id
        let disableCheckbox = marketplaceOrder?.networkStatuses[domainId] && marketplaceOrder?.networkStatuses[domainId].status !== 'PENDING'
        return (
            <div css={domainGridStyles}>
                <img
                    src={rowData.marketplace_domain_logo || "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg"}
                    alt="Logo"
                    style={{
                        width: "100px",
                        height: "auto",
                        maxHeight: "100px"
                    }}
                />
                {
                    enableDomainSelection && (
                        <Checkbox
                            checked={selectedDomains.has(rowData.id)}
                            disabled={disableCheckbox}
                            onChange={e => {
                                domainSelectionHandler({ domainId: rowData.id })
                            }}
                        />
                    )
                }
            </div>
        )
    }
    const marketplaceDomainCategoryTemplate = rowData => {
        /** @type {{name: string}[]} */
        let categories = (rowData.selected_networks || [{ name: 'N/A' }])
        return (
            <span>
                {categories.map((item, idx) => item.name).join(', ')}
            </span>
        )
    }
    const marketplaceDomainDescriptionTemplate = rowData => {
        /** @type {string} */
        let description = rowData.marketplace_domain_description || ''
        if (description.length > 100) {
            let shortenedDescription = description.slice(0, 100)
            return (
                <div css={domainDescriptionStyles}>
                    <span>{shortenedDescription}</span>
                    <Button className="p-button-icon-only p-button-text" icon="pi pi-ellipsis-h" tooltip={description} />
                </div>
            )
        }
        return <span>{description}</span>
    }
    try {
        if (!hasFinishedRetrievingDomains) {
            return (
                <div css={marketplaceDataLoadStyle} style={{ display: 'flex' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "3em", color: "#fff" }}></i>
                    <p className="message">Retrieving Marketplace domains data</p>
                </div>
            )
        }
        return (
            <DataTable
                value={domainsColl}
                className="p-datatable-gridlines"
                css={datatableStyles}
                bodyClassName="mrktplc-domains"
            >
                <Column body={marketplaceDomainImageTemplate} header="Property"></Column>
                <Column field="marketplace_domain_url" header="Domain"></Column>
                <Column body={marketplaceDomainCategoryTemplate} header="Category"></Column>
                <Column body={marketplaceDomainDescriptionTemplate} header="Description"></Column>
                <Column field="marketplace_impressions" header="Available Impressions"></Column>
            </DataTable>
        )
    } catch (err) {
        console.log(err)
        return null;
    }
};

/**
 * 
 * @param {Object} propsObject 
 * @param {boolean} propsObject.hasStaticData
 * @param {Set<string>} propsObject.selectedDomainsSet
 * @param {Array<MarketplaceDomainConfig>} propsObject.participatingDomains
 * @param {MarketplaceOrder} marketplaceOrder
 * @returns 
 */
const MarketplacePartnerScreen = ({
    allowDownloadCsvLink = true,
    availableNetworks = null,
    participatingDomains = null,
    hasStaticData = false,
    enableDomainSelection = false,
    domainSelectionHandler = () => { },
    selectedDomainsSet = new Set(),
    marketplaceOrder
}) => {

    const { currentCustomer } = useSelector((state) => state.userInformation);
    const customerName = "Ebony";
    const [retrieveMarketplaceOrdersMutation] = useMutation(MARKETPLACE_ORDERS_MUTATION);
    const [finishedRetrievingDomains, setFinishedRetrievingDomains] = useState(false)
    const [csvDataDownloadMutation] = useMutation(CSV_DATA_DOWNLOAD);
    const [allDomains, setAllDomainsInfo] = useState([]);
    const [selectedDomains, setSelectedDomains] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState([]); /* these are the options that were picked by the user of the network selection dropdown */
    const [activeNetworks, setActiveNetworks] = useState([]); /* these are all the options available to the network selection dropdown */
    const [usesSafetag, setUsesSafetag] = useState(false);
    const [search, setSearch] = useState("");
    const [downloadUrl, setDownloadUrl] = useState("");
    const linkRef = useRef(null);
    const dispatch = useDispatch();
    const [marketplaceSelectedDomains, setMarketplaceSelectedDomains] = useState(new Set())
    const [paginationData, setPaginationData] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)

    async function downloadCSVHandler() {
        try {
            let filtered = selectedFilters.length ? selectedDomains : allDomains;

            if (search && usesSafetag) {
                filtered = filtered.filter((domainData) => {
                    return domainData?.marketplace_domain_url?.includes(search) && domainData?.enable_safetag
                });
            } else if (search) {
                filtered = filtered.filter((domainData) => {
                    return domainData?.marketplace_domain_url?.includes(search)
                });
            } else if (usesSafetag) {
                filtered = filtered.filter((domainData) => {
                    return domainData?.enable_safetag
                });
            }

            if (filtered.length) {
                let csv = "Domain,URL,Categories,Impressions,Safetag,Description\n";
                filtered.forEach((domainData) => {
                    let { marketplace_domain_name, marketplace_domain_url, selected_networks, marketplace_impressions, enable_safetag, marketplace_domain_description } = domainData;
                    selected_networks = selected_networks.map((n) => decodeHtml(n.name));
                    csv += `${marketplace_domain_name},${marketplace_domain_url},"${selected_networks.join(", ")}","${marketplace_impressions}",${enable_safetag ? "Yes" : "No"},"${marketplace_domain_description}"\n`;
                });

                let response = await csvDataDownloadMutation({
                    variables: {
                        csv,
                        filename: 'marketplace_partners_'
                    },
                });

                let downloadLink = JSON.parse(response.data.downloadCsvDataMutation.downloadUrl);
                setDownloadUrl(downloadLink.url);
                setTimeout(() => {
                    linkRef.current.click();
                }, 50);
                return;
            }
            dispatch(pushMessage("Marketplace partners", "Nothing to download", "error"));
        } catch (err) {
            console.log(err)
            dispatch(pushMessage("Marketplace partners", "Nothing to download", "error"));
        }
    }

    const toggleSafetagHandler = (value) => {
        let filters = {};
        let filtering = false;
        if (selectedFilters.length) {
            selectedFilters.forEach((f) => {
                filters[f] = true;
            });
            filtering = true;
        }
        setUsesSafetag(value);

        if (!value) {
            let filteredDomains = allDomains.filter((domainData) => {
                if (filtering && domainData.enable_safetag) {
                    return domainData.selected_networks.some((n) => {
                        return n.code in filters || n.term_id in filters;
                    });
                } else {
                    return domainData.enable_safetag;
                }
            });
            setSelectedDomains(filteredDomains)
        } else {
            if (filtering) {
                let filteredDomains = allDomains.filter((domainData) => {
                    return domainData.selected_networks.some((n) => n.code in filters || n.term_id in filters);
                });
                setSelectedDomains(filteredDomains)
            } else {
                let domainsWithSafetagData = allDomains
                    .filter(domainData => {
                        return domainData.enable_safetag
                    })

                setSelectedDomains(domainsWithSafetagData)

            }
        }
    };

    useEffect(() => {
        if (enableDomainSelection) {
            setMarketplaceSelectedDomains(new Set(selectedDomainsSet))
        }
    }, [enableDomainSelection, selectedDomainsSet])


    useEffect(() => {
        if (selectedFilters.length) {
            let selected = selectedFilters.reduce((acc, curr) => {
                acc[curr] = true;
                return acc;
            }, {});

            let filteredDomains = allDomains.filter((domainData) => {
                let selectedNetworks = domainData.selected_networks;
                let isSelected = selectedNetworks.some((networkObject) => {
                    if (usesSafetag) {
                        return domainData.enable_safetag && (networkObject.code in selected || networkObject.term_id in selected);
                    } else {
                        return networkObject.code in selected || networkObject.term_id in selected;
                    }
                });
                return isSelected;
            });
            if (search) {
                filteredDomains = filteredDomains.filter((domainData) => domainData?.marketplace_domain_url?.includes(search.toLocaleLowerCase()));
            }
            setSelectedDomains(filteredDomains)
        } else {
            if (search) {
                let filteredDomains = allDomains.filter((domainData) => domainData?.marketplace_domain_url?.includes(search.toLocaleLowerCase()));
                setSelectedDomains(filteredDomains)
            }
        }
    }, [selectedFilters, usesSafetag, search]);


    useEffect(() => {
        if (hasStaticData) {
            if (participatingDomains?.length && availableNetworks?.length) {
                let [alreadySelectedDomains, toBeSelectedDomains] = participatingDomains
                    .reduce((acc, curr) => {
                        acc[selectedDomainsSet.has(curr.id) ? 0 : 1].push(curr)
                        return acc
                    }, [[], []])
                setAllDomainsInfo([].concat(alreadySelectedDomains, toBeSelectedDomains))
                setActiveNetworks(availableNetworks)
                setFinishedRetrievingDomains(true)
            }
        }
    }, [hasStaticData])

    useEffect(() => {
        if (customerName && currentCustomer && !hasStaticData) {
            getPartnersInfo({
                currentCustomer,
                customerName,
                dataMutation: retrieveMarketplaceOrdersMutation,
                page: currentPage,
                limitDomainResults: true
            })
                .then(response => {
                    let { networks, domainsInfo, pagination } = response;
                    setPaginationData(pagination)
                    setActiveNetworks(networks)
                    setAllDomainsInfo(domainsInfo)
                    setFinishedRetrievingDomains(true)
                })
        }

    }, [customerName, currentCustomer, currentPage]);

    return (

        <div
            css={css`
                    width: 90%;
                    max-width: 1400px;
                    margin: 0 auto;
                `}
        >
            <div
                className="control-bar"
                css={css`
                        margin-bottom: 1em;
                        display: flex;
                        flex-wrap: wrap;
                        align-items: center;
                        .heading {
                            margin-bottom: 0;
                        }
                        .filters {
                            margin-left: auto;
                            display: flex;
                            flex-wrap: wrap;
                        }
                        .layouts {
                            margin-right: 2em;
                            display: flex;
                            flex-wrap: wrap;
                            align-items: center;
                            .pi {
                                display: inline-block;
                                margin: 0 0.25em;
                                cursor: pointer;
                            }
                            .domain-notice {
                                padding: 0;
                                &:hover .p-button-label {
                                    opacity: 0;
                                }
                                &:focus,
                                &:enabled {
                                    box-shadow: none;
                                }
                            }
                        }
                        .category-selector {
                            width: 250px;
                        }
                    `}
            >
                <div className="title">
                    <h1 className="heading">Participating Domains</h1>
                </div>
                <div className="filters">
                    {
                        allowDownloadCsvLink && (
                            <div>
                                <Button className="p-button p-button-link" label="DOWNLOAD CSV" onClick={async () => await downloadCSVHandler()} />
                            </div>
                        )
                    }

                    <div
                        className="layouts"
                        css={css`
                                .lock-icon {
                                    width: 14px;
                                    height: auto;
                                    filter: grayscale(${usesSafetag ? "0" : "1"});
                                }
                                .active {
                                    font-weight: bold;
                                }
                                .safetag-switch-section {
                                    display: flex;
                                    align-items: center;
                                    margin-right: 4em;
                                }
                                .label,
                                .p-component,
                                .lock-icon {
                                    margin: 0 0.5em;
                                }
                            `}
                    >
                        <div className="safetag-switch-section">
                            <span className="label">
                                <b>Safetag</b>
                            </span>
                            <InputSwitch
                                checked={usesSafetag}
                                onChange={ev => {
                                    toggleSafetagHandler(ev.value)
                                }}
                            />
                            <img src={safetagLogo} alt={""} className="lock-icon" />
                        </div>
                    </div>
                    <div
                        className="filter"
                        css={css`
                                margin-right: 1em;
                                width: 250px;
                                input {
                                    width: 100%;
                                }`
                        }
                    >
                        <InputText placeholder="Search" value={search} onChange={(ev) => setSearch(ev.target.value)} />
                    </div>
                    <div className="dropdowns">
                        <div className="network-selection-controls">
                            <MultiSelect
                                className="category-selector"
                                optionLabel="name"
                                value={selectedFilters}
                                optionValue="value"
                                options={activeNetworks}
                                display="chip"
                                placeholder="All Categories"
                                onChange={(ev) => {
                                    setSelectedFilters(ev.value);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                className={`domains-list`}
                css={css`
                        &.column {
                            display: flex;
                            flex-wrap: wrap;
                        }
                        .domain-logo {
                            width: 220px;
                            height: 220px;
                            border: 1px solid #eee;
                            display: flex;
                            align-items: center;
                            img {
                                width: 80%;
                                margin: 0 auto;
                            }
                        }
                    `}
            >
                <DomainGrid
                    domainsColl={selectedFilters.length || usesSafetag || search ? selectedDomains : allDomains}
                    hasFinishedRetrievingDomains={finishedRetrievingDomains}
                    enableDomainSelection={enableDomainSelection}
                    selectedDomains={marketplaceSelectedDomains}
                    domainSelectionHandler={domainSelectionHandler}
                    marketplaceOrder={marketplaceOrder}
                />
                <a style={{ visibility: 0, position: "absolute", left: -90000000 }} rel="noreferrer" download="domains_list.csv" href={downloadUrl} ref={linkRef}>
                    Download link
                </a>
                {
                    paginationData && (
                        <Paginator
                            css={css`
                        .p-highlight {
                            background: unset !important;
                            color: unset !important;
                            border-color: unset !important;
                        }    
                        .p-paginator-pages .p-paginator-element:nth-of-type(${currentPage}) {
                            background: #E3F2FD !important;
                            border-color: #E3F2FD !important;
                            color: #495057 !important;
                        }
                    `}
                            first={0}
                            rows={20}
                            totalRecords={parseInt(paginationData.total_results)}
                            onPageChange={e => {
                                setCurrentPage(e.page + 1)
                            }}
                        />
                    )
                }
            </div>
        </div>
    );
};

export default MarketplacePartnerScreen;
